// Migrated from globals.scss `.blog-expert-form-wrapper` (sole owners: the page-authored wrapper
// Box in src/pages/blog/[blogsDetails].tsx and src/pages/use-cases/[useCase].tsx — both render
// this class directly, not via a shared component, so the styling is relocated here as one
// shared sx-constants module rather than into Form/index.tsx, which has no ancestor of its own
// to scope from). Covers the wrapper's own box styling plus every descendant override, all of
// which target elements Form/index.tsx (ContactForm) renders internally: `.form-heading`,
// `.bdn-form-field`/`.flag-phone-wrapper` (+ hover/focus/fieldset/input/placeholder/MUI-internal
// tiers), `.flag-phone-divider`, `.form-button .nft-button`/`.button-bg-primary`, `.Mui-error`,
// and `.form-outer` (+ nested `.close-icon-form`). The `.form-button .nft-button`/`.button-bg-primary`
// rule only ever matches on the blog page (submitBtnClass="button-bg-primary"); on the use-cases
// page no button carries either class, so the rule is inert there — kept in the shared constant
// anyway since duplicating it per-page would fork a single source of truth for no behavior change.
import {shadows, borderRadius, colors, overlay} from '@/styles/tokens';

export const blogExpertFormWrapperSx = {
    border: 'none !important',
    padding: '20px 16px !important',
    borderRadius: `${borderRadius.lg} !important`,
    background: `${colors.background.tag} !important`,
    position: 'sticky !important',
    top: '100px !important',
    '& .form-heading': {
        fontSize: '20px !important'
    },
    // `&&` (not `&`) here: `.common-input-css .MuiOutlinedInput-root`/`.common-input-css
    // fieldset` in globals.scss are still-global 2-class selectors with `!important` on the
    // same properties (border-color/outline); at equal specificity, source order decides, and
    // an emotion-injected rule isn't reliably ordered after the static stylesheet — verified via
    // computed style in a live page that a plain `&` lost that tie (border-color stayed #c4c4c4
    // instead of #e5e7eb). `&&` doubles the generated ancestor class for one extra specificity
    // level, making the win unconditional regardless of insertion order.
    '&& .bdn-form-field, && .flag-phone-wrapper': {
        backgroundColor: `${colors.text.inverse} !important`,
        border: `1px solid ${colors.neutral.borderLight} !important`,
        borderRadius: `${borderRadius.sm} !important`,
        color: `${colors.text.primary} !important`,
        transition: 'border-color 0.2s !important',
        '&:hover': {
            borderColor: 'rgba(31, 63, 122, 0.79) !important'
        },
        '&.Mui-focused': {
            borderColor: 'rgba(31, 63, 122, 0.79) !important',
            boxShadow: `${shadows.focusRing} !important`
        },
        '& fieldset': {
            border: 'none !important'
        },
        '& input, & textarea': {
            color: `${colors.text.primary} !important`,
            background: 'transparent !important',
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
            fontSize: '14px !important'
        },
        '&.MuiInputBase-multiline': {
            paddingTop: '2px !important',
            paddingBottom: '6px !important',
            '& textarea': {
                paddingTop: '0 !important'
            }
        },
        '& input::placeholder, & textarea::placeholder': {
            color: `${overlay.dark45} !important`,
            opacity: '1 !important',
            fontSize: '14px !important'
        },
        '& .MuiSelect-select': {
            color: `${colors.text.primary} !important`
        },
        '& .MuiSvgIcon-root': {
            color: `${overlay.dark55} !important`
        }
    },
    '& .flag-phone-divider': {
        backgroundColor: `${colors.neutral.borderLight} !important`
    },
    '& .form-button .nft-button, & .form-button .button-bg-primary': {
        width: '100% !important'
    },
    '& .Mui-error': {
        color: `${colors.accent.starRed} !important`
    },
    '& .form-outer': {
        padding: '0 !important',
        '& .close-icon-form': {
            display: 'none !important'
        }
    }
} as const;
